// Reuses the Administration page stylesheet for .admin-tabs and .admin-table*.
import "../administration/administration.css";

import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { RefreshButton, dateCell } from "../components/dataTable";
import { nextSort, sortRows } from "../administration/sortable";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AdminTable } from "../administration/AdminTable";
import CopyButton from "../components/copyButton";
import { EmptyState } from "../components/EmptyState";
import { LevelHeader } from "../clientDashboard/components/LevelHeader";
import { CfSelect as Select } from "../components/CfSelect";
import { addDangerAlert } from "../actions/alerts";
import { fetchAllBulkExports } from "../services/backendRequests";
import moment from "moment";

// Pill tone per export status, same roles as the upload sessions page.
const STATUS_TONE = {
  pending: "muted",
  processing: "warn",
  done: "ok",
  failed: "danger",
};

const STATUS_OPTIONS = Object.keys(STATUS_TONE).map((key) => ({
  value: key,
  label: key,
}));

const PERIOD_OPTIONS = [
  { value: 1, label: "Last 24 hours" },
  { value: 2, label: "Last 2 days" },
  { value: 7, label: "Last week" },
  { value: 14, label: "Last 2 weeks" },
  { value: null, label: "All" },
];

const SORT_VALUE = {
  user: (e) => e.user,
  created: (e) => e.created_at,
  campaign: (e) => e.contract_info?.name ?? `${e.campaign_count} campaigns`,
  status: (e) => e.status,
  started: (e) => e.started_at,
  finished: (e) => e.finished_at,
  since: (e) => e.updated_at,
};

const BulkExports = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [failedExport, setFailedExport] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(undefined);
  const [periodDays, setPeriodDays] = useState(14);
  const [sort, setSort] = useState({ sortKey: "created", sortDir: "desc" });
  const handleSort = (column) => setSort((prev) => nextSort(prev, column));

  const reloadData = useCallback(() => {
    setLoading(true);
    setLoadFailed(false);
    fetchAllBulkExports(user)
      .then(setExports)
      .catch(() => {
        setLoadFailed(true);
        dispatch(
          addDangerAlert("Could not load the bulk exports. Please try again.")
        );
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const since = periodDays && moment().subtract(periodDays, "days");
    const filtered = exports.filter(
      (e) =>
        (!needle || (e.user ?? "").toLowerCase().includes(needle)) &&
        (!status || e.status === status) &&
        (!since || moment(e.created_at).isAfter(since))
    );
    return sortRows(filtered, sort.sortKey, sort.sortDir, (e, key) =>
      SORT_VALUE[key](e)
    );
  }, [exports, query, status, periodDays, sort]);

  const columns = [
    {
      key: "user",
      sortable: true,
      label: "Requested by",
    },
    {
      key: "created",
      sortable: true,
      label: "Requested",
      cell: (e) => dateCell(e.created_at),
    },
    {
      // Requested-to-started is the time spent queued behind other exports.
      key: "started",
      sortable: true,
      label: "Started",
      cell: (e) => dateCell(e.started_at),
    },
    {
      key: "finished",
      sortable: true,
      label: "Finished",
      cell: (e) => dateCell(e.finished_at),
    },
    {
      key: "campaign",
      sortable: true,
      label: "Campaign",
      cell: (e) => (
        <div className="d-flex align-items-center gap-2">
          {SORT_VALUE.campaign(e)}
          <CopyButton
            small
            value={e.campaign_ids.join(", ")}
            datatip={
              e.campaign_ids.length > 1
                ? "Copy campaign ids"
                : "Copy campaign id"
            }
          />
        </div>
      ),
    },
    {
      key: "status",
      sortable: true,
      label: "Status",
      cell: (e) => (
        <div className="d-flex align-items-center gap-2">
          <span className={`cf-badge cf-badge--${STATUS_TONE[e.status]}`}>
            {e.status}
          </span>
          {e.status === "failed" && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => setFailedExport(e)}
            >
              <i
                className="fa-solid fa-circle-exclamation"
                aria-hidden="true"
              />{" "}
              View error
            </button>
          )}
        </div>
      ),
    },
    {
      // How long the export has sat in its current status: a pending one
      // that keeps aging means the jobs worker is not running.
      key: "since",
      sortable: true,
      label: "In this status since",
      cell: (e) => moment(e.updated_at).fromNow(),
    },
  ];

  return (
    <>
      <Modal
        isOpen={!!failedExport}
        toggle={() => setFailedExport(null)}
        size="lg"
      >
        <ModalHeader toggle={() => setFailedExport(null)}>
          Export error
        </ModalHeader>
        <ModalBody>
          <p>{failedExport?.error_message}</p>
          {/* Exports that failed before the backtrace was recorded only
              have the message. */}
          {failedExport?.error_backtrace ? (
            <pre className="mb-0 text-wrap">{failedExport.error_backtrace}</pre>
          ) : (
            <p className="text-muted mb-0">No backtrace recorded.</p>
          )}
        </ModalBody>
      </Modal>
      {/* Tabs and refresh share one row: a toolbar holding only the refresh
          button left a tall empty band between the tabs and the table. */}
      <div className="d-flex align-items-center mb-2">
        {/* ponytail: a single tab for now, the tab strip is kept so the next
            support tool slots in next to it. */}
        <div className="admin-tabs mb-0" role="tablist" aria-label="Support">
          <button
            type="button"
            role="tab"
            aria-selected={true}
            className="admin-tabs__btn is-active"
          >
            <i className="fa-solid fa-file-zipper" aria-hidden="true" />
            Bulk exports
          </button>
        </div>
        <RefreshButton
          className="ms-auto"
          refreshing={loading}
          onRefresh={reloadData}
          data-tooltip-id="tooltip"
          data-tooltip-content="Refresh"
        />
      </div>
      {!loading && loadFailed ? (
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Could not load the bulk exports."
          text="Use the refresh button to try again."
        />
      ) : !loading && exports.length === 0 ? (
        <EmptyState
          icon="fa-solid fa-file-zipper"
          title="No bulk export found."
          text="Exports appear here as soon as a user requests one."
        />
      ) : (
        <>
          <div className="cf-filters">
            <div className="cf-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search a user…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Search a user"
              />
            </div>
            <label className="cf-field">
              <span className="cf-field__label">Status</span>
              <Select
                className="cf-rs"
                classNamePrefix="cf-rs"
                isClearable
                isSearchable={false}
                placeholder="All statuses"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((o) => o.value === status) ?? null}
                onChange={(option) => setStatus(option?.value)}
              />
            </label>
            <label className="cf-field">
              <span className="cf-field__label">Requested</span>
              <Select
                className="cf-rs"
                classNamePrefix="cf-rs"
                isSearchable={false}
                options={PERIOD_OPTIONS}
                value={PERIOD_OPTIONS.find((o) => o.value === periodDays)}
                onChange={(option) => setPeriodDays(option.value)}
              />
            </label>
          </div>
          {!loading && visible.length === 0 ? (
            <EmptyState
              icon="fa-solid fa-filter"
              title="No bulk export matches these filters."
              text="Try adjusting the search, status or period."
            />
          ) : (
            <AdminTable
              columns={columns}
              rows={visible}
              loading={loading}
              sortKey={sort.sortKey}
              sortDir={sort.sortDir}
              onSort={handleSort}
            />
          )}
        </>
      )}
    </>
  );
};

const Support = () => (
  <div className="page-content container-fluid cf-page admin-tabbed">
    <LevelHeader path={[{ label: "Support" }]} />
    <BulkExports />
  </div>
);

export default Support;
